/**
 * Fonctions pures de calcul de métriques dashboard
 * Aucun import React - utilisables partout
 */

const PALETTE = ['#6B4C9A','#10B981','#F59E0B','#3B82F6','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316','#84CC16','#6366F1','#D97706'];

/**
 * Construit une map { [name]: color } stable (triée alphabétiquement)
 * Garantit la même couleur pour un même nom quel que soit le graphique.
 */
export function buildColorMap(names = []) {
  const unique = [...new Set(names.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
  const map = {};
  unique.forEach((name, i) => { map[name] = PALETTE[i % PALETTE.length]; });
  return map;
}

/**
 * Calcule le Health Score du projet
 * score = storyScore*0.4 + milestoneScore*0.3 + ticketScore*0.3
 * @returns {{ score, storyScore, milestoneScore, ticketScore, color }}
 */
const EXCLUDED_CATEGORIES = new Set(['Annulé']);
const EXCLUDED_PROJECTS = new Set(['retrospective', 'interne', 'assistance']);

export function computeHealthScore(usStageGroups = [], milestones = [], tickets = [], doneTasksCount = null, totalTasksCount = null, resolvedTicketsCount = null, totalTicketsCount = null) {
  // Exclure les projets internes/support et les US annulées
  const activeGroups = usStageGroups.filter(g =>
    !EXCLUDED_CATEGORIES.has(g.category) &&
    !EXCLUDED_PROJECTS.has((g.projectName || '').toLowerCase())
  );
  // Vue globale : utiliser les counts Odoo directs (couvrent tous les projets actifs/archivés)
  const totalStories = totalTasksCount !== null
    ? totalTasksCount
    : activeGroups.reduce((s, g) => s + g.count, 0);
  const doneStories = doneTasksCount !== null
    ? doneTasksCount
    : activeGroups.filter(g => g.category === 'Done').reduce((s, g) => s + g.count, 0);
  const storyScore = totalStories > 0 ? Math.round((doneStories / totalStories) * 100) : 0;

  // Milestone score : jalons atteints (completed uniquement) / total
  const totalMilestones = milestones.length;
  const completedMilestones = milestones.filter(m => m.status === 'completed').length;
  const milestoneScore = totalMilestones > 0
    ? Math.round((completedMilestones / totalMilestones) * 100)
    : 100; // Pas de jalons = pas de problème

  // Ticket score : counts directs Odoo (search_count) — aucune limite
  const agentTickets = tickets.filter(ticket => ticket.botOutcome !== 'resolved');
  const totalTickets = totalTicketsCount !== null ? totalTicketsCount : agentTickets.filter(t => t.stageId !== 5).length;
  const resolvedTickets = resolvedTicketsCount !== null ? resolvedTicketsCount : agentTickets.filter(t => t.stageId === 4 || t.stageId === 7).length;
  const ticketScore = totalTickets > 0
    ? Math.round((resolvedTickets / totalTickets) * 100)
    : 100;

  const score = Math.round(storyScore * 0.4 + milestoneScore * 0.3 + ticketScore * 0.3);

  let color = 'green';
  if (score < 40) color = 'red';
  else if (score < 70) color = 'orange';

  return {
    score, storyScore, milestoneScore, ticketScore, color,
    doneStories, totalStories,
    completedMilestones, totalMilestones,
    resolvedTickets, totalTickets,
  };
}

/**
 * Recalcule le burndown (série + KPIs) à partir des tâches de la baseline.
 * Miroir front de la logique backend, pour filtrer par projet sans re-fetch.
 * @param {Array} tasks  [{ id, name, sp, projectId, dateTest, devs, stage }]
 * @returns {Object} { sprintStart, sprintEnd, baselineSP, completedSP, remainingSP, totalUS, doneUS, series }
 */
export function computeBurndown(tasks = [], sprintStart, sprintEnd) {
  if (!sprintStart || !tasks.length) {
    return { sprintStart, sprintEnd, baselineSP: 0, completedSP: 0, remainingSP: 0, totalUS: 0, doneUS: 0, series: [] };
  }

  // Détail des US testées par jour (pour le popup)
  const testedByDate = {};
  tasks.forEach(t => {
    if (!t.dateTest) return;
    (testedByDate[t.dateTest] ||= []).push({ id: t.id, name: t.name, sp: t.sp, stage: t.stage, devs: t.devs });
  });

  const baselineSP = tasks.reduce((s, t) => s + (t.sp || 0), 0);
  const totalUS = tasks.length;
  const doneUS = tasks.filter(t => t.isDone ?? t.dateTest != null).length;

  const start = new Date(sprintStart);
  const end = new Date(sprintEnd);
  const today = new Date().toISOString().slice(0, 10);
  const totalDays = Math.max(1, Math.ceil((end - start) / 86400000));
  const dayCount = totalDays + 1;

  const completedUpTo = (d) => tasks
    .filter(t => t.dateTest != null && t.dateTest <= d)
    .reduce((s, t) => s + (t.sp || 0), 0);

  const completedSP = completedUpTo(today);
  const remainingSP = Math.max(0, baselineSP - completedSP);

  const series = [];
  for (let i = 0; i < dayCount; i++) {
    const dateStr = new Date(start.getTime() + i * 86400000).toISOString().slice(0, 10);
    const ideal = Math.max(0, Math.round(baselineSP - (baselineSP / totalDays) * i));
    const tested = testedByDate[dateStr] || [];
    if (dateStr > today) {
      series.push({ date: dateStr, remaining: null, ideal, tested });
      continue;
    }
    const rem = i === 0 ? baselineSP : Math.max(0, baselineSP - completedUpTo(dateStr));
    series.push({ date: dateStr, remaining: rem, ideal, tested });
  }

  return { sprintStart, sprintEnd, baselineSP, completedSP, remainingSP, totalUS, doneUS, series };
}

/**
 * Calcule les données du pipeline SP pour le graphique en aire
 * @returns {Array} [{ stage, storyPoints, count }]
 */
export function computeSpPipelineData(usStageGroups = [], sprintActif = false) {
  const PIPELINE_STAGES = sprintActif
    ? [
        { stage: 'Backlog Sprint',  match: g => g.category === 'Backlog Sprint' },
        { stage: 'Daily',           match: g => g.category === 'Daily' },
        { stage: 'En cours',        match: g => g.category === 'En cours' },
        { stage: 'En Test',         match: g => g.category === 'En Test' },
        { stage: 'Test PO',         match: g => g.category === 'Test PO' },
        { stage: 'Test testeur',    match: g => g.category === 'Test testeur' },
        { stage: 'Tests à valider', match: g => g.category === 'Tests à valider' },
        { stage: 'Refaisage',       match: g => g.category === 'Refaisage' },
        { stage: 'Done',            match: g => g.category === 'Done' },
      ]
    : [
        { stage: 'Raffinage',           match: g => { const s = g.stageName.toLowerCase(); return (s.includes('raffinage') || s.includes('rafinnage')) && !s.includes('po review'); } },
        { stage: 'PO Review',           match: g => g.stageName.toLowerCase().includes('po review') },
        { stage: 'Spec Generation',     match: g => g.stageName.toLowerCase().includes('spec') },
        { stage: 'Confirmation', match: g => g.category === 'Confirmation' },
        { stage: 'Backlog Produit',     match: g => g.category === 'Backlog Produit' },
        { stage: 'Backlog Sprint',      match: g => g.category === 'Backlog Sprint' },
        { stage: 'En cours',            match: g => g.category === 'En cours' },
        { stage: 'En Test',             match: g => g.category === 'En Test' },
        { stage: 'Tests à valider',     match: g => g.category === 'Tests à valider' },
      ];

  return PIPELINE_STAGES.map(({ stage, match }) => {
    const groups = usStageGroups.filter(match);
    return {
      stage,
      storyPoints: groups.reduce((sum, g) => sum + g.totalSP, 0),
      count: groups.reduce((sum, g) => sum + g.count, 0),
    };
  });
}

/**
 * Calcule la répartition SP par Jalon pour le graphique en barres horizontales empilées
 * @returns {Array} [{ name, doneSP, inProgressSP, remainingSP, totalSP, completionPct }]
 */
export function computeEpicBreakdownData(userStories = [], milestones = []) {
  const milestoneMap = {};

  milestones.forEach(m => {
    milestoneMap[m.id] = {
      id: m.id,
      name: m.name || `Jalon ${m.id}`,
      doneSP: 0,
      inProgressSP: 0,
      remainingSP: 0,
      totalSP: 0
    };
  });

  userStories.forEach(us => {
    const milestoneId = us.milestoneId;
    if (!milestoneId || !milestoneMap[milestoneId]) return;

    const sp = us.storyPoints || 0;
    milestoneMap[milestoneId].totalSP += sp;

    if (us.category === 'Done') {
      milestoneMap[milestoneId].doneSP += sp;
    } else if (us.category === 'En Test' || us.category === 'Test PO' || us.category === 'Test testeur' || us.category === 'Tests à valider') {
      milestoneMap[milestoneId].inProgressSP += sp;
    } else {
      milestoneMap[milestoneId].remainingSP += sp;
    }
  });

  return Object.values(milestoneMap)
    .map(m => ({
      ...m,
      completionPct: m.totalSP > 0
        ? Math.round((m.doneSP / m.totalSP) * 100)
        : 0
    }))
    .filter(m => m.totalSP > 0)
    .sort((a, b) => b.completionPct - a.completionPct);
}

/**
 * Calcule la distribution des tickets pour le donut
 * @returns {{ distribution, highPriorityCount, urgentCount, slaAtRisk }}
 */
export function computeTicketDistribution(tickets = []) {
  // Étapes masquées du donut (Cloturé + Annulé uniquement)
  const CLOSED_STAGE_IDS = new Set([4, 5]);

  const STAGE_COLORS = {
    1: '#EF4444',  // Nouveau
    2: '#F59E0B',  // En cours
    3: '#8B5CF6',  // En attente
    6: '#3B82F6',  // Ouvert
    7: '#10B981',  // Résolu
  };

  // Grouper uniquement les tickets ouverts
  const stageMap = {};
  tickets.forEach(ticket => {
    const id = ticket.stageId;
    if (id === undefined || CLOSED_STAGE_IDS.has(id)) return;
    if (!stageMap[id]) {
      stageMap[id] = {
        name: ticket.stageName || `Étape ${id}`,
        color: STAGE_COLORS[id] || '#6B7280',
        count: 0,
        stageId: id
      };
    }
    stageMap[id].count++;
  });

  const distribution = Object.values(stageMap)
    .filter(d => d.count > 0)
    .sort((a, b) => b.count - a.count);

  // Priorités calculées sur les tickets ouverts uniquement
  const openTickets = tickets.filter(t => !CLOSED_STAGE_IDS.has(t.stageId));
  const highPriorityCount = openTickets.filter(t => t.priority === '1').length;
  const urgentCount = openTickets.filter(t => t.priority === '2' || t.priority === '3').length;

  const now = new Date();
  const in24h = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const slaAtRisk = tickets.filter(ticket => {
    if (ticket.stageId === 4 || ticket.stageId === 7) return false;
    if (!ticket.slaDeadline) return false;
    const deadline = new Date(ticket.slaDeadline);
    return deadline <= in24h && deadline >= now;
  });

  return { distribution, highPriorityCount, urgentCount, slaAtRisk };
}

/**
 * Regroupe les tickets par client ET par statut pour barres empilées
 * @returns {{ chartData, stages, total }}
 *   chartData : [{ clientName, [stageName]: count, total }]
 *   stages    : [{ name, color }] dans l'ordre d'affichage
 */
export function computeTicketsByClient(tickets = []) {
  const EXCLUDED = new Set([4]); // Cloturé exclu

  const STAGE_ORDER = [1, 6, 2, 3, 7]; // Nouveau, Ouvert, En cours, En attente, Résolu
  const STAGE_COLORS = {
    1: '#EF4444',  // Nouveau
    2: '#3B82F6',  // En cours
    3: '#F59E0B',  // En attente
    6: '#8B5CF6',  // Ouvert
    7: '#10B981',  // Résolu
  };

  const RESOLVED_STAGES = new Set([7]); // Résolu

  // Construire map client → { [stageName]: count }
  const clientMap = {};
  const stagesSeen = new Map(); // stageId → stageName

  tickets.forEach(t => {
    if (EXCLUDED.has(t.stageId)) return;
    const client = t.clientName || 'Sans client';
    const stageName = t.stageName || `Étape ${t.stageId}`;
    if (!clientMap[client]) clientMap[client] = { clientName: client, total: 0, activeCount: 0 };
    clientMap[client][stageName] = (clientMap[client][stageName] || 0) + 1;
    clientMap[client].total++;
    if (!RESOLVED_STAGES.has(t.stageId)) clientMap[client].activeCount++;
    if (t.stageId && !stagesSeen.has(t.stageId)) stagesSeen.set(t.stageId, stageName);
  });

  // Trier les stages dans l'ordre défini
  const stages = STAGE_ORDER
    .filter(id => stagesSeen.has(id))
    .map(id => ({ name: stagesSeen.get(id), color: STAGE_COLORS[id] || '#6B7280' }));

  // Ajouter les stages non référencés (ordre quelconque)
  stagesSeen.forEach((name, id) => {
    if (!STAGE_ORDER.includes(id)) stages.push({ name, color: STAGE_COLORS[id] || '#6B7280' });
  });

  // Exclure les clients dont tous les tickets sont résolus
  const chartData = Object.values(clientMap)
    .filter(c => c.activeCount > 0)
    .sort((a, b) => b.total - a.total);

  const total = chartData.reduce((s, c) => s + c.total, 0);

  return { chartData, stages, total };
}

/**
 * Calcule la progression par client à partir des groupes US + liste des projets
 * @returns {Array} [{ clientName, totalUS, doneUS, progressPct, totalSP, doneSP }]
 */
export function computeClientProgressData(usStageGroups = [], projects = [], sprintActif = false) {
  // En mode sprint : uniquement les catégories actives du sprint (pas Raffinage, Annulé, Backlog Produit…)
  const SPRINT_CATEGORIES = new Set(['Backlog Sprint', 'Daily', 'En cours', 'En Test', 'Test PO', 'Test testeur', 'Tests à valider', 'Refaisage', 'Done']);
  const GLOBAL_CATEGORIES = new Set(['Backlog Produit', 'Backlog Sprint', 'En cours', 'En Test', 'Test PO', 'Test testeur', 'Tests à valider', 'Done']);
  const allowedCategories = sprintActif ? SPRINT_CATEGORIES : GLOBAL_CATEGORIES;

  const projectToClient = {};
  projects.forEach(p => {
    projectToClient[p.id] = p.partnerName || p.name || `Projet ${p.id}`;
  });

  const clientMap = {};
  usStageGroups
    .filter(g => allowedCategories.has(g.category))
    .forEach(g => {
      const client = projectToClient[g.projectId] || 'Sans client';
      if (!clientMap[client]) {
        clientMap[client] = { clientName: client, totalUS: 0, doneUS: 0, totalSP: 0, doneSP: 0 };
      }
      clientMap[client].totalUS += g.count;
      clientMap[client].totalSP += g.totalSP;
      if (g.category === 'Done' || g.category === 'En Test' || g.category === 'Test PO' || g.category === 'Test testeur' || g.category === 'Tests à valider') {
        clientMap[client].doneUS += g.count;
        clientMap[client].doneSP += g.totalSP;
      }
    });

  return Object.values(clientMap)
    .filter(c => c.totalUS > 0)
    .map(c => ({
      ...c,
      progressPct: c.totalUS > 0 ? Math.round((c.doneUS / c.totalUS) * 100) : 0
    }))
    .sort((a, b) => b.progressPct - a.progressPct);
}

/**
 * Calcule la vélocité SP (done vs total)
 * @returns {{ totalSP, doneSP, remainingSP, pct }}
 */
export function computeSpVelocity(userStories = []) {
  const totalSP = userStories.reduce((sum, us) => sum + (us.storyPoints || 0), 0);
  const doneSP = userStories
    .filter(us => us.category === 'Done' || us.category === 'En Test' || us.category === 'Test PO' || us.category === 'Test testeur' || us.category === 'Tests à valider')
    .reduce((sum, us) => sum + (us.storyPoints || 0), 0);
  const remainingSP = totalSP - doneSP;
  const pct = totalSP > 0 ? Math.round((doneSP / totalSP) * 100) : 0;

  return { totalSP, doneSP, remainingSP, pct };
}
